<template>
  <form @submit.prevent="submitForm" class="bf-form">
    <!-- ==================== Identity ==================== -->
    <section class="bf-section">
      <div class="section-head">
        <div class="section-title">Identity</div>
        <div class="section-sub">Display name and login username for this branch.</div>
      </div>
      <div class="section-grid">
        <div class="bf-field" :class="{ 'is-invalid': !name.isValid }">
          <label for="name">Name</label>
          <input
            type="text"
            id="name"
            v-model.trim="name.val"
            @blur="clearValidity('name')"
            class="bf-input"
            placeholder="e.g. Mumbai Central"
          />
          <p v-if="!name.isValid" class="bf-error">Name must not be empty.</p>
        </div>

        <div class="bf-field" :class="{ 'is-invalid': !username.isValid }">
          <label for="username">Username</label>
          <div v-if="mode == 'update'" class="bf-locked">
            <span class="bf-locked-val">{{ username.val }}</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="bf-lock">
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 018 0v4" />
            </svg>
          </div>
          <input
            v-else
            type="text"
            id="username"
            v-model.trim="username.val"
            @blur="clearValidity('username')"
            class="bf-input"
            placeholder="login username"
          />
          <p v-if="!username.isValid" class="bf-error">Username must not be empty.</p>
        </div>
      </div>
    </section>

    <!-- ==================== Contact ==================== -->
    <section class="bf-section">
      <div class="section-head">
        <div class="section-title">Contact</div>
        <div class="section-sub">How ops can reach this branch. Both fields are optional.</div>
      </div>
      <div class="section-grid">
        <div class="bf-field" :class="{ 'is-invalid': !email.isValid }">
          <label for="email">Email <span class="opt">optional</span></label>
          <input
            type="email"
            id="email"
            v-model.trim="email.val"
            @blur="clearValidity('email')"
            class="bf-input"
            placeholder="ops@branch.com"
          />
          <p v-if="!email.isValid" class="bf-error">{{ email.msg }}</p>
        </div>

        <div class="bf-field" :class="{ 'is-invalid': !phone.isValid }">
          <label for="phone">Phone <span class="opt">optional</span></label>
          <input
            type="text"
            id="phone"
            v-model.trim="phone.val"
            maxlength="10"
            @blur="clearValidity('phone')"
            @keypress="isNumber($event)"
            class="bf-input"
            placeholder="10-digit number"
          />
          <p v-if="!phone.isValid" class="bf-error">{{ phone.msg }}</p>
        </div>
      </div>
    </section>

    <!-- ==================== Access ==================== -->
    <section class="bf-section">
      <div class="section-head">
        <div class="section-title">Access</div>
        <div class="section-sub">
          {{ mode === 'update' ? 'Leave password empty to keep the current one unchanged.' : 'Password used for the branch login.' }}
        </div>
      </div>
      <div class="section-grid">
        <div class="bf-field bf-field-full" :class="{ 'is-invalid': !password.isValid }">
          <label for="password">Password</label>
          <input
            type="text"
            id="password"
            v-model.trim="password.val"
            @blur="clearValidity('password')"
            class="bf-input"
            :placeholder="mode === 'update' ? 'leave empty to keep unchanged' : 'set a strong password'"
          />
          <p v-if="!password.isValid" class="bf-error">Password must not be empty.</p>
        </div>
      </div>
    </section>

    <!-- ==================== Session state (update-only) ==================== -->
    <section v-if="mode == 'update'" class="bf-section">
      <div class="section-head">
        <div class="section-title">Session State</div>
        <div class="section-sub">Manage the branch's active status and forced logout.</div>
      </div>
      <div class="section-grid">
        <div class="bf-field">
          <label for="status">Status</label>
          <select v-model.trim="status.val" id="status" class="bf-input">
            <option value="1">Active</option>
            <option value="0">In Active</option>
          </select>
        </div>
        <div class="bf-field">
          <label for="isLoggedIn">Login Status</label>
          <select v-model.trim="isLoggedIn.val" id="isLoggedIn" class="bf-input">
            <option value="1">Logged In</option>
            <option value="0">Logged Out</option>
          </select>
        </div>
      </div>
    </section>

    <!-- ==================== Permissions ==================== -->
    <!-- Manual-payin-approval grant (update-only) + Routing exclusivity.
         Both are boolean flags shown as modern toggle rows. -->
    <section class="bf-section">
      <div class="section-head">
        <div class="section-title">Permissions & Routing</div>
        <div class="section-sub">Grant approval permissions and control routing exclusivity for this branch.</div>
      </div>

      <label
        v-if="mode == 'update'"
        class="bf-toggle-row"
        :class="{ 'is-on': canApprovePayinManually, 'is-off': !canApprovePayinManually }"
      >
        <div class="bf-toggle-copy">
          <div class="bf-toggle-title">
            Manual payin approval
            <span :class="canApprovePayinManually ? 'bf-toggle-badge bf-toggle-badge-on' : 'bf-toggle-badge bf-toggle-badge-off'">
              {{ canApprovePayinManually ? 'ALLOWED' : 'BLOCKED' }}
            </span>
          </div>
          <div class="bf-toggle-desc">
            When off (default), this branch must approve payins via Bank Statement upload. When on, the Approve button becomes available on payin orders.
          </div>
        </div>
        <span class="bf-switch" @click.prevent="canApprovePayinManually = !canApprovePayinManually">
          <input type="checkbox" :checked="canApprovePayinManually" class="bf-switch-input" @change.prevent />
          <span class="bf-switch-track"></span>
          <span class="bf-switch-knob"></span>
        </span>
      </label>

      <!--
        Routing exclusivity is PER DIRECTION (22072026): each toggle
        removes the branch from DEFAULT routing for that direction only.
        Mixed setups are the point — e.g. payin-exclusive + payout-open
        means "accepts every payout, but payins only via routing groups".
      -->
      <label
        class="bf-toggle-row"
        :class="{ 'is-on-danger': branchExclusivePayin, 'is-off': !branchExclusivePayin }"
      >
        <div class="bf-toggle-copy">
          <div class="bf-toggle-title">
            Pay-in — exclusive to routing groups
            <span :class="branchExclusivePayin ? 'bf-toggle-badge bf-toggle-badge-danger' : 'bf-toggle-badge bf-toggle-badge-off'">
              {{ branchExclusivePayin ? 'EXCLUSIVE' : 'OPEN' }}
            </span>
          </div>
          <div class="bf-toggle-desc">
            When off (default), this branch serves pay-in orders from any client in the vendor. When on, only clients in a routing group (pay-in or both) that includes this branch can send pay-ins here. Make sure the branch is in at least one such group first — otherwise no pay-in traffic will reach it.
          </div>
        </div>
        <span class="bf-switch" @click.prevent="branchExclusivePayin = !branchExclusivePayin">
          <input type="checkbox" :checked="branchExclusivePayin" class="bf-switch-input" @change.prevent />
          <span class="bf-switch-track"></span>
          <span class="bf-switch-knob"></span>
        </span>
      </label>

      <label
        class="bf-toggle-row"
        :class="{ 'is-on-danger': branchExclusivePayout, 'is-off': !branchExclusivePayout }"
      >
        <div class="bf-toggle-copy">
          <div class="bf-toggle-title">
            Pay-out — exclusive to routing groups
            <span :class="branchExclusivePayout ? 'bf-toggle-badge bf-toggle-badge-danger' : 'bf-toggle-badge bf-toggle-badge-off'">
              {{ branchExclusivePayout ? 'EXCLUSIVE' : 'OPEN' }}
            </span>
          </div>
          <div class="bf-toggle-desc">
            When off (default), this branch serves pay-out orders from any client in the vendor. When on, only clients in a routing group (pay-out or both) that includes this branch can send pay-outs here.
          </div>
        </div>
        <span class="bf-switch" @click.prevent="branchExclusivePayout = !branchExclusivePayout">
          <input type="checkbox" :checked="branchExclusivePayout" class="bf-switch-input" @change.prevent />
          <span class="bf-switch-track"></span>
          <span class="bf-switch-knob"></span>
        </span>
      </label>

      <!-- Partial-payout settings (20092026). Setting A: can this branch
           see/pick payouts another branch already partially settled. -->
      <label
        class="bf-toggle-row"
        :class="{ 'is-on': canPickupPartialPayout, 'is-off': !canPickupPartialPayout }"
      >
        <div class="bf-toggle-copy">
          <div class="bf-toggle-title">
            Enable half (remainder) payouts
            <span :class="canPickupPartialPayout ? 'bf-toggle-badge' : 'bf-toggle-badge bf-toggle-badge-off'">
              {{ canPickupPartialPayout ? 'ON' : 'OFF' }}
            </span>
          </div>
          <div class="bf-toggle-desc">
            When off (default), this branch does NOT see payouts that another branch has already partially paid — those remainders stay hidden. Turn on for designated cleanup branches that should handle leftover amounts.
          </div>
        </div>
        <span class="bf-switch" @click.prevent="canPickupPartialPayout = !canPickupPartialPayout">
          <input type="checkbox" :checked="canPickupPartialPayout" class="bf-switch-input" @change.prevent />
          <span class="bf-switch-track"></span>
          <span class="bf-switch-knob"></span>
        </span>
      </label>

      <!-- Setting B: can this branch itself leave a remainder (partial approve). -->
      <label
        class="bf-toggle-row"
        :class="{ 'is-on': canPartialApprovePayout, 'is-off': !canPartialApprovePayout }"
      >
        <div class="bf-toggle-copy">
          <div class="bf-toggle-title">
            Allow partial approve
            <span :class="canPartialApprovePayout ? 'bf-toggle-badge' : 'bf-toggle-badge bf-toggle-badge-off'">
              {{ canPartialApprovePayout ? 'ON' : 'OFF' }}
            </span>
          </div>
          <div class="bf-toggle-desc">
            When on (default), this branch can approve a payout for part of the amount, leaving a remainder. When off, the branch must settle the full amount in one go.
          </div>
        </div>
        <span class="bf-switch" @click.prevent="canPartialApprovePayout = !canPartialApprovePayout">
          <input type="checkbox" :checked="canPartialApprovePayout" class="bf-switch-input" @change.prevent />
          <span class="bf-switch-track"></span>
          <span class="bf-switch-knob"></span>
        </span>
      </label>

      <!-- Unlimited payout pickup (22102026): bypass the 3 in-flight cap. -->
      <label
        class="bf-toggle-row"
        :class="{ 'is-on': canPickupUnlimitedPayout, 'is-off': !canPickupUnlimitedPayout }"
      >
        <div class="bf-toggle-copy">
          <div class="bf-toggle-title">
            Unlimited payout pickup
            <span :class="canPickupUnlimitedPayout ? 'bf-toggle-badge' : 'bf-toggle-badge bf-toggle-badge-off'">
              {{ canPickupUnlimitedPayout ? 'ON' : 'OFF' }}
            </span>
          </div>
          <div class="bf-toggle-desc">
            By default a branch can hold at most 3 in-process payouts at once. When on, this branch can pick up unlimited payouts concurrently — no in-process cap.
          </div>
        </div>
        <span class="bf-switch" @click.prevent="canPickupUnlimitedPayout = !canPickupUnlimitedPayout">
          <input type="checkbox" :checked="canPickupUnlimitedPayout" class="bf-switch-input" @change.prevent />
          <span class="bf-switch-track"></span>
          <span class="bf-switch-knob"></span>
        </span>
      </label>
    </section>

    <!-- ==================== Commission & Balance ==================== -->
    <section class="bf-section">
      <div class="section-head">
        <div class="section-title">Commission & Balance</div>
        <div class="section-sub">Opening balance, daily pay-in limit, and commission rates.</div>
      </div>
      <div class="section-grid">
        <div class="bf-field" :class="{ 'is-invalid': !openingBalance.isValid }">
          <label for="opening-balance">Opening Balance</label>
          <div v-if="mode == 'update'" class="bf-locked">
            <span class="bf-locked-val">₹ {{ openingBalance.val }}</span>
            <span class="bf-locked-hint">Read-only</span>
          </div>
          <div v-else class="bf-input-suffix">
            <span class="bf-prefix">₹</span>
            <input
              type="number"
              id="opening-balance"
              v-model.trim="openingBalance.val"
              min="0"
              @blur="clearValidity('openingBalance')"
              class="bf-input bf-input-num"
              placeholder="0"
            />
          </div>
          <p v-if="!openingBalance.isValid" class="bf-error">Opening Balance must not be empty.</p>
        </div>

        <div class="bf-field" :class="{ 'is-invalid': !payInLimit.isValid }">
          <label for="pay-in-limit">Pay-In Limit <span class="opt">branch-level</span></label>
          <div class="bf-input-suffix">
            <span class="bf-prefix">₹</span>
            <input
              type="number"
              id="pay-in-limit"
              v-model.trim="payInLimit.val"
              min="0"
              step="0.01"
              @blur="clearValidity('payInLimit')"
              class="bf-input bf-input-num"
              placeholder="0"
            />
          </div>
          <p v-if="!payInLimit.isValid" class="bf-error">{{ payInLimit.msg }}</p>
        </div>

        <div class="bf-field" :class="{ 'is-invalid': !payInCommissionPercentage.isValid }">
          <label for="pay-in-commission-percentage">Pay-In Commission</label>
          <div class="bf-input-suffix">
            <input
              type="number"
              id="pay-in-commission-percentage"
              v-model.trim="payInCommissionPercentage.val"
              min="0"
              step="0.01"
              @blur="clearValidity('payInCommissionPercentage')"
              class="bf-input bf-input-num"
              placeholder="0.00"
            />
            <span class="bf-suffix">%</span>
          </div>
          <p v-if="!payInCommissionPercentage.isValid" class="bf-error">Pay In Commission Percentage must not be empty.</p>
        </div>

        <div class="bf-field" :class="{ 'is-invalid': !payOutCommissionPercentage.isValid }">
          <label for="pay-out-commission-percentage">Pay-Out Commission</label>
          <div class="bf-input-suffix">
            <input
              type="number"
              id="pay-out-commission-percentage"
              v-model.trim="payOutCommissionPercentage.val"
              min="0"
              step="0.01"
              @blur="clearValidity('payOutCommissionPercentage')"
              class="bf-input bf-input-num"
              placeholder="0.00"
            />
            <span class="bf-suffix">%</span>
          </div>
          <p v-if="!payOutCommissionPercentage.isValid" class="bf-error">Pay Out Commission Percentage must not be empty.</p>
        </div>
      </div>
    </section>

    <!-- ==================== Submit ==================== -->
    <div class="bf-submit">
      <p v-if="!formIsValid" class="bf-submit-error">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v4M12 16h.01" stroke-linecap="round" />
        </svg>
        Please fix the highlighted errors and submit again.
      </p>
      <button type="submit" class="btn btn-primary">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" class="btn-icon">
          <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        {{ mode === 'create' ? 'Create Branch' : 'Save Changes' }}
      </button>
    </div>
  </form>
</template>

<script>
export default {
  emits: ["create-user"],
  props: ["mode", "selectedUser"],
  mounted() {
    if (this.mode == "update") {
      this.setUser();
    }
  },
  data() {
    return {
      name: {
        val: "",
        isValid: true,
        msg: "",
      },
      username: {
        val: "",
        isValid: true,
        msg: "",
      },
      email: {
        val: "",
        isValid: true,
        msg: "",
      },
      phone: {
        val: "",
        isValid: true,
        msg: "",
      },
      password: {
        val: "",
        isValid: true,
        msg: "",
      },
      status: {
        val: true,
        isValid: true,
        msg: "",
      },
      isLoggedIn: {
        val: false,
        isValid: true,
        msg: "",
      },
      openingBalance: {
        val: 0,
        isValid: true,
        msg: "",
      },
      payInLimit: {
        val: 0,
        isValid: true,
        msg: "",
      },
      payInCommissionPercentage: {
        val: 0,
        isValid: true,
        msg: "",
      },
      payOutCommissionPercentage: {
        val: 0,
        isValid: true,
        msg: "",
      },
      // Per-branch manual-approve permission. Bound directly (no
      // isValid/msg pair) — it's a binary toggle the backend
      // validates. Only meaningful in update mode.
      canApprovePayinManually: false,
      // UI toggles are the LOGICAL INVERSE of the DB flags. DB stores
      // `branch_serves_default_payin` / `branch_serves_default_payout`
      // (1 = serves default traffic for that direction); the UI
      // checkboxes read "Exclusive to routing groups" which corresponds
      // to the flag being 0. Per-direction since 22072026 — a branch
      // can be payin-exclusive while staying open for payouts.
      branchExclusivePayin: false,
      branchExclusivePayout: false,
      // Partial-payout settings (20092026). Bound directly to the DB flags
      // (unlike the exclusivity toggles, these are NOT inverted).
      // Defaults here mirror the column defaults for the create path:
      // pickup OFF, partial-approve ON.
      canPickupPartialPayout: false,
      canPartialApprovePayout: true,
      canPickupUnlimitedPayout: false,
      formIsValid: true,
    };
  },
  methods: {
    clearValidity(input) {
      this[input].isValid = true;
      this[input].msg = "";
    },
    validateForm() {
      this.formIsValid = true;

      if (this.mode == "create") {
        if (this.username.val === "") {
          this.username.isValid = false;
          this.formIsValid = false;
        }
        if (this.password.val === "") {
          this.password.isValid = false;
          this.formIsValid = false;
        }
      }

      if (this.name.val === "") {
        this.name.isValid = false;
        this.formIsValid = false;
      }
      if (this.phone.val === "" && this.phone.val !== "") {
        this.phone.isValid = false;
        this.phone.msg = "Phone must not be empty.";
        this.formIsValid = false;
      } else if (this.phone.val.length > 0 && this.phone.val.length < 10) {
        this.phone.isValid = false;
        this.phone.msg = "Phone number must be 10 digits.";
        this.formIsValid = false;
      }
    },
    submitForm() {
      this.validateForm();

      if (!this.formIsValid) {
        return;
      }

      let formData = {
        username: this.username.val,
        name: this.name.val,
        email: this.email.val,
        phone: this.phone.val,
        status: this.status.val,
        isLoggedIn: this.isLoggedIn.val,
        openingBalance: this.openingBalance.val,
        payInLimit: this.payInLimit.val,
        payInCommissionPercentage: this.payInCommissionPercentage.val,
        payOutCommissionPercentage: this.payOutCommissionPercentage.val,
      };

      // Manual-approve permission is only edited in update mode and only
      // when the toggle actually moves — avoids spurious audit-log rows.
      if (this.mode === "update") {
        const original = !!(this.selectedUser && this.selectedUser.can_approve_payin_manually);
        if (this.canApprovePayinManually !== original) {
          formData.can_approve_payin_manually = this.canApprovePayinManually ? 1 : 0;
        }
      }

      // Routing exclusivity — per-direction flags. Sent on create only
      // when exclusive (DB default of 1 covers the open case) and on
      // update only when the toggle actually moved. Inverted: UI ON = DB 0.
      if (this.mode === "create") {
        if (this.branchExclusivePayin) {
          formData.branch_serves_default_payin = 0;
        }
        if (this.branchExclusivePayout) {
          formData.branch_serves_default_payout = 0;
        }
      } else {
        const su = this.selectedUser || {};
        // Fall back to the legacy direction-blind flag for rows the API
        // served before the 22072026 migration ran.
        const legacy = su.branch_serves_default != null ? Number(su.branch_serves_default) : 1;
        const originalPayin = su.branch_serves_default_payin != null
          ? Number(su.branch_serves_default_payin)
          : legacy;
        const originalPayout = su.branch_serves_default_payout != null
          ? Number(su.branch_serves_default_payout)
          : legacy;
        const nextPayin = this.branchExclusivePayin ? 0 : 1;
        const nextPayout = this.branchExclusivePayout ? 0 : 1;
        if (nextPayin !== originalPayin) {
          formData.branch_serves_default_payin = nextPayin;
        }
        if (nextPayout !== originalPayout) {
          formData.branch_serves_default_payout = nextPayout;
        }
      }

      // Partial-payout settings (20092026). Direct (non-inverted) flags.
      // Create: send only non-default values (DB defaults A=0, B=1 cover
      // the rest). Update: send only when the toggle actually moved, so we
      // don't write unchanged flags.
      if (this.mode === "create") {
        if (this.canPickupPartialPayout) formData.can_pickup_partial_payout = 1;
        if (!this.canPartialApprovePayout) formData.can_partial_approve_payout = 0;
        if (this.canPickupUnlimitedPayout) formData.can_pickup_unlimited_payout = 1;
      } else {
        const su = this.selectedUser || {};
        const origPickup = Number(su.can_pickup_partial_payout || 0) === 1;
        // Default ON when the column is absent/legacy-null.
        const origPartial = su.can_partial_approve_payout != null
          ? Number(su.can_partial_approve_payout) === 1
          : true;
        const origUnlimited = Number(su.can_pickup_unlimited_payout || 0) === 1;
        if (this.canPickupPartialPayout !== origPickup) {
          formData.can_pickup_partial_payout = this.canPickupPartialPayout ? 1 : 0;
        }
        if (this.canPartialApprovePayout !== origPartial) {
          formData.can_partial_approve_payout = this.canPartialApprovePayout ? 1 : 0;
        }
        if (this.canPickupUnlimitedPayout !== origUnlimited) {
          formData.can_pickup_unlimited_payout = this.canPickupUnlimitedPayout ? 1 : 0;
        }
      }

      if (this.mode == "create") {
        formData.password = this.password.val;
      } else {
        if (this.password.val != "") {
          formData.password = this.password.val;
        }
      }

      this.$emit("create-user", formData);
    },
    isNumber: function(evt) {
      evt = evt ? evt : window.event;
      var charCode = evt.which ? evt.which : evt.keyCode;
      if (
        charCode > 31 &&
        (charCode < 48 || charCode > 57) &&
        charCode !== 46
      ) {
        evt.preventDefault();
      } else {
        return true;
      }
    },
    setUser() {
      this.name.val = this.selectedUser.name;
      this.username.val = this.selectedUser.username;
      this.email.val = this.selectedUser.email;
      this.phone.val = this.selectedUser.phone;
      this.status.val = this.selectedUser.status;
      this.isLoggedIn.val = this.selectedUser.isLoggedIn;
      this.openingBalance.val = this.selectedUser.balance || 0;
      this.payInLimit.val = this.selectedUser.payInLimit || 0;
      this.payInCommissionPercentage.val = this.selectedUser.payInCommission || 0;
      this.payOutCommissionPercentage.val = this.selectedUser.payOutCommission || 0;
      // TINYINT(1) comes back as 0/1; coerce to bool for the checkbox.
      this.canApprovePayinManually = !!this.selectedUser.can_approve_payin_manually;
      // Partial-payout settings (20092026). Direct (non-inverted). Pickup
      // defaults OFF; partial-approve defaults ON when the column is
      // absent/null (legacy rows served before this migration).
      this.canPickupPartialPayout = Number(this.selectedUser.can_pickup_partial_payout || 0) === 1;
      this.canPartialApprovePayout = this.selectedUser.can_partial_approve_payout != null
        ? Number(this.selectedUser.can_partial_approve_payout) === 1
        : true;
      // Unlimited payout pickup (22102026) — defaults OFF when absent/legacy.
      this.canPickupUnlimitedPayout = Number(this.selectedUser.can_pickup_unlimited_payout || 0) === 1;
      // Per-direction flags: 0 → exclusive for that direction; absent
      // (rows served before the 22072026 migration) falls back to the
      // legacy direction-blind flag; absent everywhere → not exclusive.
      // UI toggles are the inverse of the DB values.
      const legacyBsd = this.selectedUser.branch_serves_default;
      const legacyExclusive = legacyBsd != null && Number(legacyBsd) === 0;
      const payinFlag = this.selectedUser.branch_serves_default_payin;
      const payoutFlag = this.selectedUser.branch_serves_default_payout;
      this.branchExclusivePayin = payinFlag != null ? Number(payinFlag) === 0 : legacyExclusive;
      this.branchExclusivePayout = payoutFlag != null ? Number(payoutFlag) === 0 : legacyExclusive;
    },
  },
};
</script>

<style scoped>
/* Form shell */
.bf-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 4px 20px 20px;
  max-width: 1100px;
  margin: 0 auto;
  width: 100%;
}

/* Section card */
.bf-section {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px 22px;
}
.section-head { margin-bottom: 16px; }
.section-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
}
.section-sub {
  font-size: 12px;
  color: #64748b;
  margin-top: 3px;
}

/* Grid */
.section-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 20px;
}
@media (max-width: 720px) {
  .section-grid { grid-template-columns: 1fr; }
}

/* Field */
.bf-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.bf-field-full { grid-column: 1 / -1; }
.bf-field label {
  font-size: 11px;
  font-weight: 600;
  color: #475569;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
}
.opt {
  font-size: 10px;
  font-weight: 500;
  color: #94a3b8;
  letter-spacing: 0.04em;
  text-transform: none;
}

/* Input */
.bf-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #f8fafc;
  outline: none;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  font-family: inherit;
  color: #0f172a;
}
.bf-input::placeholder { color: #94a3b8; }
.bf-input:focus {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(27, 42, 71, 0.14);
}
.bf-input-num { font-variant-numeric: tabular-nums; }
select.bf-input {
  appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' d='M1 1l5 5 5-5'/></svg>");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 32px;
}

/* Invalid */
.bf-field.is-invalid .bf-input,
.bf-field.is-invalid .bf-locked,
.bf-field.is-invalid .bf-input-suffix {
  border-color: #ef4444;
  background: #fef2f2;
}
.bf-field.is-invalid .bf-input:focus {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.14);
}
.bf-error {
  font-size: 11px;
  color: #dc2626;
  margin-top: 2px;
  font-weight: 500;
}

/* Locked (read-only display) */
.bf-locked {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f1f5f9;
  color: #475569;
  font-size: 13px;
}
.bf-locked-val {
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.bf-locked-hint {
  font-size: 10px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
  flex-shrink: 0;
}
.bf-lock {
  width: 14px; height: 14px;
  color: #94a3b8;
  flex-shrink: 0;
}

/* Input with prefix / suffix */
.bf-input-suffix {
  display: flex;
  align-items: stretch;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  overflow: hidden;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.bf-input-suffix:focus-within {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(27, 42, 71, 0.14);
}
.bf-input-suffix .bf-input {
  border: none;
  background: transparent;
  border-radius: 0;
  flex: 1;
  box-shadow: none;
  padding-left: 4px;
  padding-right: 4px;
}
.bf-input-suffix .bf-input:focus {
  background: transparent;
  box-shadow: none;
}
.bf-prefix {
  display: inline-flex;
  align-items: center;
  padding: 0 10px 0 14px;
  color: #64748b;
  font-size: 13px;
  font-weight: 700;
  background: transparent;
  border-right: 1px solid #eef2f7;
}
.bf-suffix {
  display: inline-flex;
  align-items: center;
  padding: 0 14px;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  background: transparent;
  border-left: 1px solid #eef2f7;
}

/* Toggle rows */
.bf-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  cursor: pointer;
  transition: all 0.15s;
}
.bf-toggle-row + .bf-toggle-row { margin-top: 10px; }
.bf-toggle-row.is-on {
  border-color: #a7f3d0;
  background: #ecfdf5;
}
.bf-toggle-row.is-on-danger {
  border-color: #fecaca;
  background: #fef2f2;
}
.bf-toggle-copy { min-width: 0; }
.bf-toggle-title {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.bf-toggle-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
}
.bf-toggle-badge-on { background: #d1fae5; color: #065f46; }
.bf-toggle-badge-off { background: #f1f5f9; color: #64748b; }
.bf-toggle-badge-danger { background: #fee2e2; color: #991b1b; }
.bf-toggle-desc {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
  line-height: 1.5;
}

/* iOS-style switch */
.bf-switch {
  position: relative;
  display: inline-block;
  width: 44px; height: 24px;
  flex-shrink: 0;
  cursor: pointer;
}
.bf-switch-input {
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
  position: absolute;
}
.bf-switch-track {
  position: absolute;
  inset: 0;
  border-radius: 999px;
  background: #cbd5e1;
  transition: background 0.18s;
}
.bf-switch-knob {
  position: absolute;
  top: 2px; left: 2px;
  width: 20px; height: 20px;
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.2);
  transition: transform 0.18s;
}
.bf-switch-input:checked ~ .bf-switch-track { background: #10b981; }
.bf-switch-input:checked ~ .bf-switch-knob { transform: translateX(20px); }
.is-on-danger .bf-switch-input:checked ~ .bf-switch-track { background: #ef4444; }

/* Submit */
.bf-submit {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  padding: 8px 0 4px;
}
.bf-submit-error {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  font-size: 12px;
  font-weight: 500;
  align-self: stretch;
  justify-content: center;
}
.bf-submit-error svg { width: 14px; height: 14px; }

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
  outline: none;
}
.btn-primary {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 6px 16px -6px rgba(27, 42, 71, 0.5);
}
.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px -8px rgba(27, 42, 71, 0.55);
}
.btn-icon { width: 15px; height: 15px; }
</style>
